import { DRONE_CATALOG, isDroneItem, shipHasDefaultDroneBay } from "./droneSystem";
import type { NpcHullTier } from "./types";

/**
 * Which drones a captain should even be offered (16.97, Don's direction).
 *
 * Drones are the one market item with a hard compatibility rule: a bay only
 * ever loads the drone matching its own hull tier (`loadableDroneCount`), and
 * only Diggers and Packmules carry a bay at all. So three of the four catalogue
 * entries are, for any given captain, an expensive brick -- and the Market was
 * offering all four to everyone, including to a Kestrel pilot who can never fit
 * one.
 *
 * That is not merely clutter. A Capital Drone is 20,900 CR, which is half a
 * starting hull, and nothing on the screen said it would do nothing.
 *
 * The rule Don asked for:
 *   - the flown ship's own drone, if it has a bay;
 *   - plus any drone a ship in the FLEET could use, since a captain provisions
 *     for ships they are not currently sitting in;
 *   - and nothing else.
 *
 * This module is deliberately pure and hull-shaped rather than reading `state`,
 * so the rule is testable and has exactly one definition.
 */

export interface DroneCapableShip {
  role: string;
  hullTier: NpcHullTier;
}

/**
 * Drone item names this captain has some ship for, across the flown hull and
 * the whole fleet. A ship with no bay contributes nothing even at a matching
 * tier -- the tier is only half the rule.
 */
export function usableDroneNames(ships: Array<DroneCapableShip | null | undefined>): Set<string> {
  const names = new Set<string>();
  ships.forEach((ship) => {
    if (!ship || !shipHasDefaultDroneBay(ship.role)) return;
    const entry = DRONE_CATALOG.find((d) => d.hullTier === ship.hullTier);
    if (entry) names.add(entry.name);
  });
  return names;
}

/**
 * Should this market row be listed at all?
 *
 * Non-drones always list. A drone lists when some ship can use it -- OR when
 * the captain is already carrying it, because a hold that cannot be emptied is
 * worse than a cluttered table. Selling is how you recover from having bought
 * the wrong thing, or from selling the ship that fitted the right thing, and
 * hiding the row would strand the cargo permanently.
 */
export function droneRowVisible(itemName: string, usable: Set<string>, heldQty: number): boolean {
  if (!isDroneItem(itemName)) return true;
  return usable.has(itemName) || heldQty > 0;
}

/**
 * Can this drone be BOUGHT here? Visible-because-held is not permission to
 * acquire more: you can always liquidate an incompatible drone, never
 * accumulate one. This is the buy-side half of the size lock that
 * `loadableDroneCount` enforces on the bay side.
 */
export function droneBuyAllowed(itemName: string, usable: Set<string>): boolean {
  if (!isDroneItem(itemName)) return true;
  return usable.has(itemName);
}
